import React from 'react';

import { makeStyles, useTheme, darken } from '@material-ui/core/styles';

import BarChart from './BarChart';
import PieChart from './PieChart';
import LineChart from './LineChart';
import LineTimeChart from './LineTimeChart';
import { Translate } from '@hydra/common/components';
import { CriteriumSkeleton } from '@hydra/common/criterium';
import { useReviewContext } from '@hydra/common/context';

// using width 99% to make container resize properly https://github.com/recharts/recharts/issues/172#issuecomment-307858843
const CHART_OPTIONS = {
  width: '99%',
  height: 300
};

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(1.25),
    // This makes the rest of the review resize smoothly https://github.com/recharts/recharts/issues/1767
    width: '100%',
    position: 'relative',
    height: CHART_OPTIONS.height
  },
  chart: {
    width: '100%',
    position: 'absolute'
  },
  legendSpan: {
    marginRight: theme.spacing(0.75),
    color: darken(theme.palette.grey[900], 0.3)
  }
}));

type ChartType = 'bar' | 'line' | 'time' | 'pie';

interface ChartData {
  x: string
  y: number | string
  day: string
  month: string
}

function getChartComponent (type: ChartType) {
  const chartComponents = {
    bar: BarChart,
    line: LineChart,
    time: LineTimeChart,
    pie: PieChart
  };

  return chartComponents[type];
}

function useChartColors (type: ChartType) {
  const theme = useTheme();
  const chartColors = {
    bar: [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main
    ],
    line: [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.competitor.main
    ],
    time: [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.competitor.main
    ],
    pie: [
      theme.palette.primary.main,
      theme.palette.primary.light,
      theme.palette.primary.smoke
    ]
  };

  return chartColors[type];
}

function findValueFor (data: ChartData[], x: string) {
  const found = data.find(item => item.x === x);
  return found ? found.y : 0;
}

export function parseArrayData (chartData: { name: string, data: ChartData[] }[]) {
  const xKeys = chartData.reduce(
    (result: ChartData[], { data }: { data: ChartData[] }) => {
      return data.length > result.length ? data : result;
    },
    []
  );

  let index = 0;
  return chartData.reduce(
    (result: { data: { x: string }[], charts: { dataKey: string, name: string }[] }, { data, name }: { name: string, data: ChartData[] }) => {
      const nextIndex = `data-${++index}`;
      const nextCharts = result.charts.concat({ dataKey: nextIndex, name });

      const nextData = result.data.length === 0
        ? xKeys.map(({ x, day, month }) => ({ [nextIndex]: findValueFor(data, x), x, day, month }))
        : result.data.map(oldData => ({ ...oldData, [nextIndex]: findValueFor(data, oldData.x) }));

      return {
        data: nextData,
        charts: nextCharts
      };
    },
    { data: [], charts: [] }
  );
}

export function parseObjectData (chartData: { name: string, data: ChartData[] }) {
  const { data, name } = chartData;
  return {
    data,
    charts: [{ dataKey: 'y', name }]
  };
}

export default function CriteriumChart ({
  type,
  data,
  hasLegend = false,
  hasTooltip = false,
  frequency,
  chartOptions = CHART_OPTIONS,
  colors
}: {
  type: ChartType
  data: any
  hasLegend?: boolean
  hasTooltip?: boolean
  frequency?: 'day' | 'month'
  chartOptions?: {
    width: string | number,
    height: number
  }
  colors?: string[]
}) {
  const classes = useStyles();
  const { variant } = useReviewContext();
  const ChartComponent = getChartComponent(type);
  const chartData = Array.isArray(data) ? parseArrayData(data) : parseObjectData(data);
  const defaultColors = useChartColors(type);
  const chartColors = colors || defaultColors;

  if (!ChartComponent) {
    return null;
  }
  const legendFormatter = (value: string) => {
    return <span className={classes.legendSpan}>{value}</span>;
  };

  const hasData = chartData.data.length > 0;

  return (
    <div className={classes.root}>
      {hasData
        ? (
          <div className={classes.chart}>
            {
              variant === 'nocrit'
                ? <CriteriumSkeleton variant='rect' style={{ height: '150px' }} />
                : <ChartComponent
                    chartOptions={chartOptions}
                    chartData={chartData}
                    hasLegend={hasLegend}
                    hasTooltip={hasTooltip}
                    colors={chartColors}
                    frequency={frequency}
                    legendFormatter={legendFormatter}
                  />
            }
          </div>
          )
        : (variant === 'nocrit' ? <CriteriumSkeleton width='60%' /> : <Translate content='missing' />)}
    </div>
  );
}
